/**
 * <angel-quiz> — Six-question Angel Number quiz.
 *
 * Commands (dispatched via commandfor/command on descendant buttons):
 *   --answer     — Record the clicked answer (data-primary +2, data-secondary +1) and advance.
 *   --back       — Return to the previous question, discarding its answer.
 *   --restart    — Clear answers and return to question one.
 *   --share      — Open the device share sheet for the visible result.
 *   --copy-code  — Copy the promo code in the button's data-copy.
 *   --copy-link  — Copy the visible result's share text and link.
 *
 * Required child markup:
 *   [data-quiz]            — Wrapper for the question steps.
 *   [data-step]            — One per question, in order.
 *   [data-result="1111"]   — One result panel per angel number, hidden by default.
 *   [data-native-share]    — Revealed when the Web Share API is available.
 *   [data-announcer]       — Polite live region for copy confirmations.
 */
class AngelQuiz extends HTMLElement {
  #ac;
  #steps = [];
  #answers = [];
  #index = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#steps = [...this.querySelectorAll('[data-step]')];

    if (navigator.share) {
      this.querySelectorAll('[data-native-share]').forEach((el) => (el.hidden = false));
    }

    this.addEventListener('command', (event) => this.#onCommand(event), { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #onCommand(event) {
    const source = event.source;
    switch (event.command) {
      case '--answer':
        this.#answers[this.#index] = [source.dataset.primary, source.dataset.secondary];
        if (this.#index < this.#steps.length - 1) {
          this.#showStep(this.#index + 1);
        } else {
          this.#finish();
        }
        break;
      case '--back':
        this.#showStep(Math.max(0, this.#index - 1));
        break;
      case '--restart':
        this.#answers = [];
        this.#visibleResult()?.setAttribute('hidden', '');
        this.querySelector('[data-quiz]').hidden = false;
        this.#showStep(0);
        break;
      case '--share':
        this.#share();
        break;
      case '--copy-code':
        this.#copy(source.dataset.copy, source);
        break;
      case '--copy-link': {
        const result = this.#visibleResult();
        if (result) this.#copy(`${result.dataset.shareText} ${result.dataset.shareUrl}`, source);
        break;
      }
    }
  }

  #showStep(index) {
    this.#index = index;
    this.#answers.length = index;
    this.#steps.forEach((step, i) => (step.hidden = i !== index));
    this.#focus(this.#steps[index].querySelector('h2'));
  }

  #finish() {
    const scores = {};
    for (const [primary, secondary] of this.#answers) {
      scores[primary] = (scores[primary] || 0) + 2;
      scores[secondary] = (scores[secondary] || 0) + 1;
    }
    const best = Math.max(...Object.values(scores));
    const tied = Object.keys(scores).filter((n) => scores[n] === best);
    const [lastPrimary, lastSecondary] = this.#answers[this.#answers.length - 1];
    let winner = tied.includes(lastPrimary) ? lastPrimary : tied.includes(lastSecondary) ? lastSecondary : tied.sort()[0];

    this.querySelector('[data-quiz]').hidden = true;
    const panel = this.querySelector(`[data-result="${winner}"]`);
    if (!panel) return;
    panel.hidden = false;
    this.#focus(panel.querySelector('h2'));
  }

  #visibleResult() {
    return this.querySelector('[data-result]:not([hidden])');
  }

  async #share() {
    const result = this.#visibleResult();
    if (!result || !navigator.share) return;
    try {
      await navigator.share({
        title: result.dataset.shareTitle,
        text: result.dataset.shareText,
        url: result.dataset.shareUrl,
      });
    } catch {
      // Closing the share sheet rejects; nothing to do.
    }
  }

  async #copy(text, button) {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      return;
    }
    const label = button?.querySelector('[data-label]');
    const confirmation = button?.dataset.copiedLabel;
    if (label && confirmation) {
      const original = label.textContent;
      label.textContent = confirmation;
      setTimeout(() => (label.textContent = original), 2000);
    }
    const announcer = this.querySelector('[data-announcer]');
    if (announcer && confirmation) announcer.textContent = confirmation;
  }

  #focus(el) {
    if (!el) return;
    el.focus({ preventScroll: true });
    const top = this.getBoundingClientRect().top + window.scrollY - 120;
    if (this.getBoundingClientRect().top < 0) {
      window.scrollTo({ top, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
  }
}

if (!customElements.get('angel-quiz')) customElements.define('angel-quiz', AngelQuiz);
